/**
 * Dagre layout adapter for non-swimlane graph layout.
 * Uses @dagrejs/dagre for hierarchical arrangement.
 */

import type { IRGraph, LayoutResult, LayoutConfig } from '../types';
import dagre from '@dagrejs/dagre';

let dagreModule: any = dagre;

/** Map LayoutConfig direction to dagre rankdir */
function toRankdir(dir: LayoutConfig['direction']): string {
  switch (dir) {
    case 'TB':
    case 'TD':
      return 'TB';
    case 'BT':
      return 'BT';
    case 'LR':
      return 'LR';
    case 'RL':
      return 'RL';
    default:
      return 'TB';
  }
}

/**
 * Synchronous dagre layout. Assumes dagre has been loaded.
 * For the web app, dagre is bundled so this is fine.
 */
export function dagreLayout(graph: IRGraph, config: LayoutConfig): LayoutResult {
  const dagre = dagreModule;
  const g = new dagre.graphlib.Graph();

  const graphOpts: Record<string, unknown> = {
    rankdir: toRankdir(config.direction),
    nodesep: config.nodesep,
    ranksep: config.ranksep,
    edgesep: config.edgesep,
    marginx: config.marginx,
    marginy: config.marginy,
    ranker: config.ranker,
  };
  if (config.align) {
    graphOpts.align = config.align;
  }

  g.setGraph(graphOpts);
  g.setDefaultEdgeLabel(() => ({}));

  // Add nodes
  for (const [id, node] of graph.nodes) {
    g.setNode(id, { width: node.width, height: node.height });
  }

  // Add edges
  for (const edge of graph.edges) {
    if (graph.nodes.has(edge.source) && graph.nodes.has(edge.target)) {
      g.setEdge(edge.source, edge.target);
    }
  }

  dagre.layout(g);

  // Apply positions back to IR nodes
  for (const [id, node] of graph.nodes) {
    const dagreNode = g.node(id);
    if (dagreNode) {
      node.x = dagreNode.x - node.width / 2;
      node.y = dagreNode.y - node.height / 2;
    }
  }

  // Extract edge route points from dagre into edge.meta.routePoints
  for (const edge of graph.edges) {
    if (graph.nodes.has(edge.source) && graph.nodes.has(edge.target)) {
      const dagreEdge = g.edge(edge.source, edge.target);
      if (dagreEdge?.points && dagreEdge.points.length > 0) {
        if (!edge.meta) edge.meta = {};
        edge.meta.routePoints = dagreEdge.points.map((p: { x: number; y: number }) => ({
          x: p.x,
          y: p.y,
        }));
      }
    }
  }

  const graphInfo = g.graph();
  const mx = config.marginx;
  const my = config.marginy;

  return {
    graph,
    containerX: mx,
    containerY: my,
    containerWidth: (graphInfo.width ?? 400) + mx * 2,
    containerHeight: (graphInfo.height ?? 300) + my * 2,
  };
}

/** Simple fallback grid layout when dagre isn't available */
function simpleGridLayout(graph: IRGraph): LayoutResult {
  let x = 40;
  let y = 40;
  let maxW = 0;
  const maxNodeWidth = Math.max(...[...graph.nodes.values()].map(n => n.width), 200);
  const colWidth = maxNodeWidth + 40;
  const rowHeight = 100;
  let col = 0;
  const maxCols = 4;

  for (const node of graph.nodes.values()) {
    node.x = x;
    node.y = y;
    maxW = Math.max(maxW, x + node.width);
    col++;
    if (col >= maxCols) {
      col = 0;
      x = 40;
      y += rowHeight;
    } else {
      x += colWidth;
    }
  }

  return {
    graph,
    containerX: 0,
    containerY: 0,
    containerWidth: maxW + 80,
    containerHeight: y + rowHeight + 80,
  };
}
